import { ip4Regex, ip6Regex } from '@va/constants';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { isEqual } from 'lodash';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useAddNewIgnoredIp, useGetIgnoredIps, useRemoveIgnoredIp } from './apiClient';

export const useIgnoredIPs = () => {
  const {
    data: currentIgnoredIPs,
    isLoading: isLoadingCurrentIgnoredIPs,
    mutate: mutateIgnoredIps,
  } = useGetIgnoredIps();
  const [ignoredIPs, setIgnoredIPs] = useState<string[] | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoadingUpdateCall, setIsLoadingUpdateCall] = useState(false);

  const { showSuccessNotification, showErrorNotification } = useAddNotification();
  const translate = useTranslate();

  const { addNewIgnoredIp } = useAddNewIgnoredIp();
  const { removeIgnoredIp } = useRemoveIgnoredIp();

  const isInitialState = useMemo(() => ignoredIPs === null, [ignoredIPs]);

  const hasMadeChanges = useMemo(
    () => (isLoadingCurrentIgnoredIPs || isInitialState ? false : !isEqual(currentIgnoredIPs, ignoredIPs)),
    [currentIgnoredIPs, ignoredIPs, isInitialState, isLoadingCurrentIgnoredIPs],
  );

  // Populating the initial state
  useEffect(() => {
    if (!currentIgnoredIPs) return;
    setIgnoredIPs(currentIgnoredIPs);
  }, [currentIgnoredIPs]);

  const onSave = useCallback(() => {
    if (!ignoredIPs || !currentIgnoredIPs) return;
    const newAddedIps = getNewAddedIgnoredIPs(ignoredIPs, currentIgnoredIPs);
    const deletedIPs = getDeletedIgnoredIPs(ignoredIPs, currentIgnoredIPs);

    const promises: Promise<void>[] = [];

    setIsLoadingUpdateCall(true);
    newAddedIps.forEach((ip) => {
      promises.push(addNewIgnoredIp(ip));
    });

    deletedIPs.forEach((ip) => {
      promises.push(removeIgnoredIp(ip));
    });
    Promise.all(promises)
      .then(() => {
        showSuccessNotification(2000);
      })
      .catch(() => {
        showErrorNotification(2000);
      })
      .finally(() => {
        setIsLoadingUpdateCall(false);
        mutateIgnoredIps(ignoredIPs);
      });
  }, [
    addNewIgnoredIp,
    currentIgnoredIPs,
    ignoredIPs,
    mutateIgnoredIps,
    removeIgnoredIp,
    showErrorNotification,
    showSuccessNotification,
  ]);

  const onChange = (value: string) => {
    setInputValue(value);
  };

  const onRemove = (ip: string) => {
    setIgnoredIPs((prev) => {
      if (!prev) return null;
      return prev.filter((item) => item !== ip);
    });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!ignoredIPs) return;
    const isValidIp = ip4Regex.test(inputValue) || ip6Regex.test(inputValue);
    if (!isValidIp) {
      setErrorHandler(translate('card.ignoreSelfVisits.ignoreIp.ipAddressOrRangeNotValid'));
      return;
    }
    const isAlreadyIgnored = ignoredIPs.includes(inputValue);
    if (isAlreadyIgnored) {
      setErrorHandler(translate('card.ignoreSelfVisits.alreadyAddedIpAddressOrRangeMessage'));
      return;
    }
    setIgnoredIPs((prev) => {
      if (!prev) return null;
      return [...prev, inputValue];
    });
    clearInputField();
    setSubmitSuccess(true);
    setTimeout(() => {
      setSubmitSuccess(false);
    }, 500);
  };

  const clearInputField = useCallback(() => {
    setInputValue('');
  }, []);

  const setErrorHandler = (error: string) => {
    setError(error);
    setTimeout(() => {
      setError('');
    }, 3000);
  };

  return {
    onSave,
    onSubmit,
    onRemove,
    onChange,
    submitSuccess,
    isLoadingCurrentIgnoredIPs,
    inputValue,
    ignoredIPs,
    error,
    hasMadeChanges,
    isLoadingUpdateCall,
  };
};

function getDeletedIgnoredIPs(newIPs: string[], currentIPs: string[]) {
  return currentIPs.filter((ip) => !newIPs.includes(ip));
}
function getNewAddedIgnoredIPs(newIPs: string[], currentIPs: string[]) {
  return newIPs.filter((ip) => !currentIPs.includes(ip));
}
